import React, { useRef, useState, useEffect } from 'react';
import { Box, Typography, FormControl, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import { CalendarToday, Close } from '@mui/icons-material';
import { colors } from '../../../theme/theme';
import { getInputChromeStyles, getInputFocusStyles } from './InputChromeStyles';
import {
  getAccessibilityInputHover,
  InputAccessibilityHoverProps,
} from './AccessibilityHoverStyles';
import { useAccessibility } from '../../../contexts/AccessibilityContext';
import FieldHelperText from './FieldHelperText';

interface DatePickerProps extends InputAccessibilityHoverProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  maxWidth?: string | number;
  width?: string | number;
  minWidth?: string | number;
  min?: string;
  max?: string;
  fontSize?: string;
  fontFamily?: string;
  fontWeight?: number;
  fontColor?: string;
}

const DatePickerContainer = styled(FormControl, {
  shouldForwardProp: (prop) =>
    prop !== 'maxWidth' && prop !== 'width' && prop !== 'minWidth',
})<{
  maxWidth?: string | number;
  width?: string | number;
  minWidth?: string | number;
}>(({ theme, maxWidth, width, minWidth }) => ({
  position: 'relative',
  width: width || '100%',
  maxWidth: maxWidth || '600px',
  minWidth: minWidth,
}));

const DateInputWrapper = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== 'error' &&
    prop !== 'disabled' &&
    ![
      'accessibilityMode',
      'accessibilityHoverDisabled',
      'accessibilityHoverBorder',
      'accessibilityHoverBorderColor',
      'accessibilityHoverBackgroundColor',
      'accessibilityHoverColor',
      'accessibilityHoverGlow',
    ].includes(prop as string),
})<
  { error?: boolean; disabled?: boolean } & InputAccessibilityHoverProps & {
      accessibilityMode?: boolean;
    }
>(({ theme, error, disabled, ...accessibilityProps }) => {
  const chrome = getInputChromeStyles(theme, { error });
  return {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: disabled
      ? theme.palette.mode === 'dark'
        ? theme.palette.grey[800]
        : theme.palette.grey[100]
      : theme.palette.mode === 'dark'
        ? theme.palette.grey[900]
        : theme.palette.grey[50],
    ...chrome,
    padding: '0 0.25rem',
    minHeight: '48px',
    opacity: disabled ? 0.6 : 1,
    cursor: disabled ? 'not-allowed' : 'text',
    '&:hover':
      disabled || error
        ? {}
        : getAccessibilityInputHover(
            theme,
            Boolean(accessibilityProps.accessibilityMode),
            accessibilityProps
          ),
    // :focus-within, not :focus - the wrapper itself isn't focusable, the
    // day/month/year segments inside it are.
    '&:focus-within':
      disabled || error
        ? {}
        : {
            ...getInputFocusStyles(theme, chrome.boxShadow),
            ...getAccessibilityInputHover(
              theme,
              Boolean(accessibilityProps.accessibilityMode),
              accessibilityProps
            ),
          },
  };
});

const SegmentInput = styled('input', {
  shouldForwardProp: (prop) =>
    ![
      'isActive',
      'segmentWidth',
      'fontSize',
      'fontFamily',
      'fontWeight',
      'fontColor',
    ].includes(prop as string),
})<{
  isActive?: boolean;
  segmentWidth?: number;
  fontSize?: string;
  fontFamily?: string;
  fontWeight?: number;
  fontColor?: string;
}>(
  ({
    theme,
    isActive,
    segmentWidth,
    fontSize = '2rem',
    fontFamily,
    fontWeight = 600,
    fontColor,
  }) => ({
    border: 'none',
    outline: 'none',
    background: isActive
      ? theme.palette.mode === 'dark'
        ? colors.grey[50]
        : colors.secondary.main
      : 'transparent',
    color: isActive
      ? theme.palette.mode === 'dark'
        ? colors.secondary.dark
        : colors.grey[50]
      : fontColor || theme.palette.text.primary,
    fontSize,
    fontWeight,
    fontFamily: fontFamily || 'inherit',
    textAlign: 'center',
    width: `${segmentWidth}ch`,
    padding: 0,
    borderRadius: '0.125rem',
    transition: 'all 0.2s ease',
    cursor: 'pointer',
    caretColor: isActive
      ? theme.palette.mode === 'dark'
        ? colors.secondary.dark
        : colors.grey[50]
      : fontColor || theme.palette.text.primary,

    '&::placeholder': {
      color: theme.palette.text.secondary,
      opacity: 0.5,
    },

    '&:disabled': {
      cursor: 'not-allowed',
      color: theme.palette.text.disabled,
    },
  })
);

const Divider = styled(Typography, {
  shouldForwardProp: (prop) =>
    !['fontSize', 'fontFamily', 'fontWeight', 'fontColor'].includes(
      prop as string
    ),
})<{
  fontSize?: string;
  fontFamily?: string;
  fontWeight?: number;
  fontColor?: string;
}>(({ theme, fontSize = '2rem', fontFamily, fontWeight = 600, fontColor }) => ({
  fontSize,
  fontWeight,
  fontFamily: fontFamily || 'inherit',
  color: fontColor || theme.palette.text.primary,
  margin: '0 0.0625rem',
  userSelect: 'none',
}));

const CalendarIconButton = styled(IconButton)(({ theme }) => ({
  padding: '0.5rem',
  marginLeft: '0.125rem',
  transition:
    'transform 0.25s ease, box-shadow 0.25s ease, background-color 0.25s ease',
  '&:hover': {
    backgroundColor: theme.palette.primary.main,
    transform: 'scale(1.15)',
    boxShadow:
      theme.palette.mode === 'dark'
        ? `0 0 0 4px ${theme.palette.grey[900]}, 0 0 0 6px ${theme.palette.primary.light}, 0 0 12px 6px ${theme.palette.primary.light}40`
        : `0 0 0 4px #fff, 0 0 0 6px ${theme.palette.primary.main}, 0 0 12px 6px ${theme.palette.primary.main}35`,
    '& svg': {
      color: '#fff',
    },
  },
  '& svg': {
    fontSize: '2rem',
    color:
      theme.palette.mode === 'dark'
        ? theme.palette.primary.light
        : theme.palette.primary.main,
  },
}));

const ClearButton = styled(IconButton)(({ theme }) => ({
  padding: '0.5rem',
  marginLeft: '0.125rem',
  '& svg': {
    fontSize: '2rem',
    color:
      theme.palette.mode === 'dark'
        ? theme.palette.grey[400]
        : theme.palette.grey[600],
  },
  '&:hover': {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : theme.palette.primary.main,
    '& svg': {
      color: '#fff',
    },
  },
}));

const HiddenInput = styled('input')({
  position: 'absolute',
  opacity: 0,
  pointerEvents: 'none',
  width: 0,
  height: 0,
  visibility: 'hidden',
  tabIndex: -1,
});

const isoToSegments = (
  isoDate: string
): { day: string; month: string; year: string } => {
  if (!isoDate || !/^\d{4}-\d{2}-\d{2}$/.test(isoDate)) {
    return { day: '', month: '', year: '' };
  }
  const [year, month, day] = isoDate.split('-');
  return { day, month, year };
};

const segmentsToISO = (day: string, month: string, year: string): string => {
  if (!day || !month || !year) return '';
  const paddedDay = day.padStart(2, '0');
  const paddedMonth = month.padStart(2, '0');
  const paddedYear = year.padStart(4, '0');
  return `${paddedYear}-${paddedMonth}-${paddedDay}`;
};

const isValidDate = (day: string, month: string, year: string): boolean => {
  const dayNum = parseInt(day, 10);
  const monthNum = parseInt(month, 10);
  const yearNum = parseInt(year, 10);

  if (!dayNum || !monthNum || !yearNum) return false;
  if (monthNum < 1 || monthNum > 12) return false;
  if (dayNum < 1 || dayNum > 31) return false;
  if (yearNum < 1900 || yearNum > 2100) return false;

  const isoDate = segmentsToISO(day, month, year);
  const date = new Date(isoDate + 'T00:00:00');

  return (
    date.getFullYear() === yearNum &&
    date.getMonth() + 1 === monthNum &&
    date.getDate() === dayNum
  );
};

type ActiveField = 'day' | 'month' | 'year' | null;

const DatePicker: React.FC<DatePickerProps> = ({
  value,
  onChange,
  disabled = false,
  error = false,
  helperText,
  maxWidth,
  width,
  minWidth,
  min,
  max,
  fontSize,
  fontFamily,
  fontWeight,
  fontColor,
  accessibilityHoverDisabled,
  accessibilityHoverBorder,
  accessibilityHoverBorderColor,
  accessibilityHoverBackgroundColor,
  accessibilityHoverColor,
  accessibilityHoverGlow,
}) => {
  const { accessibilityMode } = useAccessibility();
  const accessibilityHoverProps = {
    accessibilityHoverDisabled,
    accessibilityHoverBorder,
    accessibilityHoverBorderColor,
    accessibilityHoverBackgroundColor,
    accessibilityHoverColor,
    accessibilityHoverGlow,
  };
  const hiddenInputRef = useRef<HTMLInputElement>(null);
  const dayInputRef = useRef<HTMLInputElement>(null);
  const monthInputRef = useRef<HTMLInputElement>(null);
  const yearInputRef = useRef<HTMLInputElement>(null);

  const [day, setDay] = useState<string>('');
  const [month, setMonth] = useState<string>('');
  const [year, setYear] = useState<string>('');
  const [activeField, setActiveField] = useState<ActiveField>(null);
  const [localError, setLocalError] = useState<boolean>(false);

  useEffect(() => {
    const segments = isoToSegments(value);
    setDay(segments.day);
    setMonth(segments.month);
    setYear(segments.year);
  }, [value]);

  const handleCalendarClick = () => {
    if (!disabled && hiddenInputRef.current) {
      const input = hiddenInputRef.current;
      if (
        'showPicker' in input &&
        typeof (input as any).showPicker === 'function'
      ) {
        try {
          (input as any).showPicker();
        } catch {
          input.focus();
          input.click();
        }
      } else {
        input.focus();
        input.click();
      }
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!disabled) {
      setDay('');
      setMonth('');
      setYear('');
      setLocalError(false);
      onChange('');
      setActiveField(null);
    }
  };

  const handleHiddenInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isoDate = e.target.value;
    const segments = isoToSegments(isoDate);
    setDay(segments.day);
    setMonth(segments.month);
    setYear(segments.year);
    setLocalError(false);
    onChange(isoDate);
  };

  const updateISODate = (newDay: string, newMonth: string, newYear: string) => {
    if (newDay && newMonth && newYear) {
      if (isValidDate(newDay, newMonth, newYear)) {
        const isoDate = segmentsToISO(newDay, newMonth, newYear);

        if (min && isoDate < min) {
          setLocalError(true);
          return;
        }
        if (max && isoDate > max) {
          setLocalError(true);
          return;
        }

        onChange(isoDate);
        setLocalError(false);
      } else {
        setLocalError(true);
      }
    } else if (!newDay && !newMonth && !newYear) {
      onChange('');
      setLocalError(false);
    }
  };

  const handleFieldFocus = (field: ActiveField) => {
    if (!disabled) {
      setActiveField(field);
      if (field === 'day') {
        setDay('');
      } else if (field === 'month') {
        setMonth('');
      } else if (field === 'year') {
        setYear('');
      }
    }
  };

  const handleDayChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value.replace(/\D/g, '').slice(0, 2);
    setDay(newValue);
    if (newValue.length === 2) {
      monthInputRef.current?.focus();
    }
    updateISODate(newValue, month, year);
  };

  const handleMonthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value.replace(/\D/g, '').slice(0, 2);
    setMonth(newValue);
    if (newValue.length === 2) {
      yearInputRef.current?.focus();
    }
    updateISODate(day, newValue, year);
  };

  const handleYearChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value.replace(/\D/g, '').slice(0, 4);
    setYear(newValue);
    updateISODate(day, month, newValue);
  };

  const handleDayKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && day === '') {
      e.preventDefault();
    }
  };

  const handleMonthKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && month === '') {
      e.preventDefault();
      dayInputRef.current?.focus();
    }
  };

  const handleYearKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && year === '') {
      e.preventDefault();
      monthInputRef.current?.focus();
    }
  };

  const hasValue = Boolean(day || month || year);
  const showError = error || localError;

  return (
    <DatePickerContainer maxWidth={maxWidth} width={width} minWidth={minWidth}>
      <DateInputWrapper
        error={showError}
        disabled={disabled}
        accessibilityMode={accessibilityMode}
        {...accessibilityHoverProps}
      >
        <SegmentInput
          ref={dayInputRef}
          type="text"
          inputMode="numeric"
          value={day}
          onChange={handleDayChange}
          onFocus={() => handleFieldFocus('day')}
          onBlur={() => setActiveField(null)}
          onKeyDown={handleDayKeyDown}
          placeholder={activeField === null ? 'dd' : ''}
          disabled={disabled}
          isActive={activeField === 'day'}
          segmentWidth={2}
          fontSize={fontSize}
          fontFamily={fontFamily}
          fontWeight={fontWeight}
          fontColor={fontColor}
          maxLength={2}
          aria-label="Day"
        />

        <Divider
          fontSize={fontSize}
          fontFamily={fontFamily}
          fontWeight={fontWeight}
          fontColor={fontColor}
        >
          /
        </Divider>

        <SegmentInput
          ref={monthInputRef}
          type="text"
          inputMode="numeric"
          value={month}
          onChange={handleMonthChange}
          onFocus={() => handleFieldFocus('month')}
          onBlur={() => setActiveField(null)}
          onKeyDown={handleMonthKeyDown}
          placeholder={activeField === null ? 'mm' : ''}
          disabled={disabled}
          isActive={activeField === 'month'}
          segmentWidth={2}
          fontSize={fontSize}
          fontFamily={fontFamily}
          fontWeight={fontWeight}
          fontColor={fontColor}
          maxLength={2}
          aria-label="Month"
        />

        <Divider
          fontSize={fontSize}
          fontFamily={fontFamily}
          fontWeight={fontWeight}
          fontColor={fontColor}
        >
          /
        </Divider>

        <SegmentInput
          ref={yearInputRef}
          type="text"
          inputMode="numeric"
          value={year}
          onChange={handleYearChange}
          onFocus={() => handleFieldFocus('year')}
          onBlur={() => setActiveField(null)}
          onKeyDown={handleYearKeyDown}
          placeholder={activeField === null ? 'yyyy' : ''}
          disabled={disabled}
          isActive={activeField === 'year'}
          segmentWidth={4}
          fontSize={fontSize}
          fontFamily={fontFamily}
          fontWeight={fontWeight}
          fontColor={fontColor}
          maxLength={4}
          aria-label="Year"
        />

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            ml: 'auto',
            mr: '1rem',
          }}
        >
          {hasValue && !disabled && (
            <ClearButton
              onClick={handleClear}
              size="small"
              aria-label="Clear date"
            >
              <Close />
            </ClearButton>
          )}
          <CalendarIconButton
            onClick={handleCalendarClick}
            disabled={disabled}
            aria-label="Open calendar"
            edge="end"
          >
            <CalendarToday />
          </CalendarIconButton>
        </Box>
      </DateInputWrapper>

      <HiddenInput
        ref={hiddenInputRef}
        type="date"
        value={value}
        onChange={handleHiddenInputChange}
        disabled={disabled}
        min={min}
        max={max}
        tabIndex={-1}
      />

      <FieldHelperText
        helperText={
          localError && !helperText
            ? 'Invalid date. Please check day, month, and year.'
            : helperText
        }
        error={showError}
      />
    </DatePickerContainer>
  );
};

export default DatePicker;
